import { useState, useCallback } from 'react';
import { useGameSocket } from '../hooks/useGameSocket';
import { useGameStore } from '../store/gameStore';
import { useSound } from '../contexts/SoundContext';
import { useHaptics } from '../hooks/useHaptics';
import { MegaBuzzer } from '../components/game/MegaBuzzer';
import { TEAM_COLORS, type TeamId } from '../types/game';

interface Props { gameId: string; team: TeamId }

export function TeamView({ gameId, team }: Props) {
  const { send } = useGameSocket(gameId, team, 'team');
  const { game, connected } = useGameStore();
  const sound = useSound();
  const haptics = useHaptics();
  const [flash, setFlash] = useState(false);

  const color = TEAM_COLORS[team];
  const isWinner = game.winner?.team === team && game.status === 'team_answering';
  const isBlocked = game.blockedTeams.includes(team);
  const hasAttempted = game.attemptedTeams.includes(team);
  const currentTurnTeam = game.teamOrder?.[game.currentTurnIndex ?? 0];
  const isMyTurn = currentTurnTeam === team;
  const isStealMode = game.roundPhase === 'steal';
  const isStealActive = isStealMode && game.status === 'steal_mode';
  const canBuzz = isStealActive && game.buzzer === 'open' && !isBlocked && !hasAttempted;

  const getBuzzerStatus = (): 'idle' | 'active' | 'winner' | 'blocked' | 'turn' => {
    if (isWinner) return 'winner';
    if (isBlocked || hasAttempted) return 'blocked';
    if (isMyTurn && !isStealMode) return 'turn';
    if (canBuzz) return 'active';
    return 'idle';
  };

  const handleBuzz = useCallback(() => {
    send({ type: 'BUZZ', team });
    haptics.buzz();
    sound.play('buzzer');
    setFlash(true);
    setTimeout(() => setFlash(false), 600);
  }, [send, team, haptics, sound]);

  return (
    <div 
      className="h-screen w-screen overflow-hidden bg-bg-deep bg-grid-tech flex flex-col"
      style={{ '--team-color': color.bg } as React.CSSProperties}
    >
      {flash && (
        <div 
          className="fixed inset-0 pointer-events-none animate-flash z-50"
          style={{ background: color.bg }}
        />
      )}

      {/* Ultra minimalist header */}
      <header className="p-6 flex items-center justify-between opacity-50 transition-opacity hover:opacity-100 flex-shrink-0">
        <div className="flex items-baseline gap-4">
          <div className="text-3xl font-black uppercase tracking-tighter" style={{ color: color.bg }}>
            EQUIPO {team}
          </div>
          <div className="text-white/60 font-bold tracking-widest text-lg">
            {game.scores[team]} PTS
          </div>
        </div>
        <div 
          className={`w-4 h-4 rounded-full shadow-[0_0_10px_currentColor] ${connected ? 'text-success bg-success' : 'text-danger bg-danger'}`} 
        />
      </header>

      {/* Main buzzer area */}
      <main className="flex-1 flex flex-col items-center justify-center p-6 relative">
        <div className="absolute top-8 flex flex-col items-center gap-3 opacity-60 text-center px-4">
          {game.currentSubject && (
            <span className="text-sm font-black tracking-[0.2em] uppercase" style={{ color: game.currentSubject.color }}>
              CATEGORÍA: {game.currentSubject.name}
            </span>
          )}
          {game.isSuddenDeath && !isBlocked && !hasAttempted && (
            <span className="text-sm font-black tracking-[0.2em] uppercase text-error animate-pulse">
              🔥 MUERTE SÚBITA 🔥
            </span>
          )}
          {game.isSuddenDeath && (isBlocked || hasAttempted) && (
            <span className="text-sm font-black tracking-[0.2em] uppercase text-muted">
              ELIMINADO DEL DESEMPATE
            </span>
          )}
          {!game.isSuddenDeath && getBuzzerStatus() === 'turn' && (
            <span className="text-sm font-black tracking-[0.2em] uppercase text-white animate-pulse">
              TU TURNO DE RESPONDER
            </span>
          )}
          {!game.isSuddenDeath && isStealMode && !isBlocked && !hasAttempted && (
            <span className="text-sm font-black tracking-[0.2em] uppercase text-warning animate-pulse">
              PREPÁRATE PARA ROBAR
            </span>
          )}
        </div>

        <MegaBuzzer
          status={getBuzzerStatus()}
          team={team}
          onPress={handleBuzz}
        />
      </main>
    </div>
  );
}
